import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import toNumber from 'lodash/toNumber';
import toString from 'lodash/toString';
import { useCallback, useMemo } from 'react';

export type CurrencyFormatterHook = (currency: number | string | undefined | null) => string;

export const CurrencySign = {
  USD: '$',
};

interface UseCurrencyFormatterProps {
  locale?: string;
  currency?: string;
  spaceBetweenCurrency?: boolean;
  placeholder?: string;
  postfix?: string | undefined;
  showPlaceholderForZero?: boolean;
}

export const useCurrencyFormatter = ({
  locale = 'en-US',
  currency = 'USD',
  spaceBetweenCurrency = false,
  placeholder = '',
  postfix = '/hour',
  showPlaceholderForZero = false,
}: UseCurrencyFormatterProps = {}) => {
  const baseFormatter = useMemo(() => {
    return {
      withDecimals: new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      noDecimals: new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    };
  }, [locale, currency]);

  const formatCurrency = useCallback<CurrencyFormatterHook>(
    (value): string => {
      if (!value || (!isNumber(value) && !isString(value))) return placeholder;
      if (isNaN(toNumber(value)) || !value) return toString(value);

      const numValue = toNumber(value);
      const isZero = numValue === 0;
      // Check if the number has decimal places
      const hasDecimals = numValue % 1 !== 0;
      const formatter = hasDecimals ? baseFormatter.withDecimals : baseFormatter.noDecimals;
      if (isZero && showPlaceholderForZero) {
        return placeholder;
      }
      const formattedValue = formatter.format(numValue);

      return spaceBetweenCurrency
        ? formattedValue.replace(CurrencySign[currency], `${CurrencySign[currency]} `)
        : formattedValue;
    },
    [currency, baseFormatter, placeholder, spaceBetweenCurrency]
  );

  const formatCurrencyPostfix = useCallback<CurrencyFormatterHook>(
    (value) => {
      const formatedValue = formatCurrency(value);
      return formatedValue && formatedValue !== placeholder
        ? `${formatCurrency(value)}${postfix}`
        : formatedValue;
    },
    [formatCurrency, postfix, placeholder]
  );

  return { formatCurrency, formatCurrencyPostfix };
};
